import {FlexColumn, Text} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import type {RunOutcome} from './novaInvocationStore';

export interface FailurePresentationProps {
	outcome: RunOutcome;
}

const CAUSE_MESSAGE_ID: Record<string, string> = {
	missing_access: 'nova_invocation.failure.cause.missing_access',
	timeout: 'nova_invocation.failure.cause.timeout',
	unavailable_model: 'nova_invocation.failure.cause.unavailable_model',
	permission: 'nova_invocation.failure.cause.permission',
	document_processing: 'nova_invocation.failure.cause.document_processing',
	at_capacity: 'nova_invocation.failure.cause.at_capacity',
};

// Self-contained and prop-driven, matching RoutingDisclosure/BasedOnList — no host renders this
// yet, since presenting a run's outcome belongs inside a conversation view that does not exist
// in this codebase yet. Built and tested standalone until one does.
export const FailurePresentation = (props: FailurePresentationProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const {outcome} = props;

	if (outcome.kind === 'succeeded') return null;

	if (outcome.kind === 'cancelled') {
		return (
			<FlexColumn data-cy="nova-cancelled">
				<Text>{formatMessage({id: 'nova_invocation.cancelled.title'})}</Text>
				<Text>{formatMessage({id: 'nova_invocation.cancelled.nothing_posted'})}</Text>
			</FlexColumn>
		);
	}

	const causeMessageId = CAUSE_MESSAGE_ID[outcome.cause] ?? 'nova_invocation.failure.cause.generic';
	return (
		<FlexColumn data-cy="nova-failure">
			<Text>{formatMessage({id: 'nova_invocation.failure.title'})}</Text>
			<Text>{formatMessage({id: causeMessageId})}</Text>
			<Text>{formatMessage({id: 'nova_invocation.failure.nothing_posted'})}</Text>
			<Text>{formatMessage({id: 'nova_invocation.failure.invoke_again'})}</Text>
		</FlexColumn>
	);
};
